// Firebase core and services
import { initializeApp, getApps, getApp } from 'firebase/app';
import { initializeAuth, getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged, updateProfile } from 'firebase/auth';
import { getFirestore, doc, setDoc, serverTimestamp, addDoc, collection, initializeFirestore, memoryLocalCache, onSnapshot as _rawOnSnapshot } from 'firebase/firestore';
import { getDatabase } from 'firebase/database';
import { getFunctions } from 'firebase/functions';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Your config (provided by user)
const firebaseConfig = {
  apiKey: 'AIzaSyDS0giRaKNZCqUS3QVeglEoG2jI6xvsxmI',
  authDomain: 'bet-optimized-fe922.firebaseapp.com',
  databaseURL: 'https://bet-optimized-fe922-default-rtdb.firebaseio.com',
  projectId: 'bet-optimized-fe922',
  storageBucket: 'bet-optimized-fe922.appspot.com',
  messagingSenderId: '726893409427',
  appId: '1:726893409427:web:063b7dcffa255f1ccbc966',
  measurementId: 'G-64THM916HX',
};

// Initialize app (singleton)
let app = getApps().length ? getApp() : initializeApp(firebaseConfig);

// Auth: native/web safe initialization
let auth;
if (typeof window === 'undefined') {
  // Native/Node environment
  try {
    auth = initializeAuth(app, { persistence: undefined });
  } catch (e) {
    auth = getAuth(app);
  }
} else {
  // Web – use default web persistence
  auth = getAuth(app);
}

// Use memory-only cache: no IndexedDB cross-session target state to conflict with.
// The getUserDocOnce singleton in AuthContext uses onSnapshot (not getDoc) which
// registers a persistent target that doesn't get recycled within a session.
let db;
try {
  db = initializeFirestore(app, {
    localCache: memoryLocalCache(),
  });
} catch (error) {
  db = getFirestore(app);
}

// Resilient onSnapshot: retries on "Target ID already exists" errors caused by
// React Strict Mode's unmount/remount race with the SDK's async target cleanup.
const _isTargetIdError = (error) =>
  error?.code === 'already-exists' ||
  (error?.message && error.message.includes('Target ID already exists'));

export function resilientOnSnapshot(ref, onData, onError, { maxRetries = 3, delayMs = 500 } = {}) {
  let currentUnsub = null;
  let cancelled = false;
  let attempt = 0;

  function tryListen() {
    if (cancelled) return;
    try {
      currentUnsub = _rawOnSnapshot(ref, (snap) => {
        if (!cancelled) onData(snap);
      }, (error) => {
        if (cancelled) return;
        if (_isTargetIdError(error) && attempt < maxRetries) {
          attempt++;
          setTimeout(tryListen, delayMs * attempt);
        } else {
          onError(error);
        }
      });
    } catch (error) {
      if (!cancelled && _isTargetIdError(error) && attempt < maxRetries) {
        attempt++;
        setTimeout(tryListen, delayMs * attempt);
      } else if (!cancelled) {
        onError(error);
      }
    }
  }

  tryListen();

  return () => {
    cancelled = true;
    if (currentUnsub) {
      try { currentUnsub(); } catch {}
    }
  };
}

// Ensure a users/{uid} document exists with matching UID
async function ensureUserDocument(uid, data = {}) {
  const userRef = doc(db, 'users', uid);
  await setDoc(userRef, { uid, createdAt: serverTimestamp(), ...data }, { merge: true });
  return userRef;
}

// Email/password registration helper
export async function registerWithEmail(email, password, profile = {}) {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  if (profile.displayName) {
    try { await updateProfile(cred.user, { displayName: profile.displayName }); } catch {}
  }
  await ensureUserDocument(cred.user.uid, { email, ...profile });
  return cred.user;
}

// Email/password sign in helper (will not recreate doc)
export async function signInWithEmailHelper(email, password) {
  const cred = await signInWithEmailAndPassword(auth, email, password);
  await ensureUserDocument(cred.user.uid, { email });
  return cred.user;
}

// Send password reset email using the public (rate-limited, non-enumerating)
// backend endpoint. The admin variant requires a Bearer token and is only used
// from the admin console.
export async function sendPasswordReset(email, actionCodeSettings = {}) {
  try {
    const response = await fetch('https://us-central1-bet-optimized-fe922.cloudfunctions.net/requestPasswordReset', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || 'Failed to send reset email');
    }

    return true;
  } catch (error) {
    console.error('Password reset error:', error);
    throw error;
  }
}

// Auth headers for admin-only Cloud Function endpoints. The backend verifies
// the Firebase ID token AND that users/{uid}.userType === 'admin'.
export async function getAdminAuthHeaders() {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const idToken = await user.getIdToken();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  };
}

// JSON headers with the signed-in user's ID token when there is one (quota,
// billing and analytics endpoints verify it). Never throws.
export async function authJsonHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const user = auth.currentUser;
    if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;
  } catch { /* anonymous */ }
  return headers;
}

// Email capture helper
export async function saveEmailCapture(email) {
  const ref = await addDoc(collection(db, 'emailCaptures'), {
    email,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export { app, auth, db, rtdb, fns, onAuthStateChanged };

// Firestore SDK bug workaround: versions 12.2–12.6+ have a race condition in the
// internal WatchChangeAggregator that triggers "INTERNAL ASSERTION FAILED: Unexpected
// state (ID: ca9) CONTEXT: {"ve":-1}" when a Firestore listener hits a permission-denied
// response. Once triggered, the SDK is poisoned — all subsequent Firestore ops fail.
// Recovery: auto-reload the page to reinitialize the SDK cleanly.
// Ref: https://github.com/firebase/firebase-js-sdk/issues/9267
if (typeof window !== 'undefined') {
  const _isFirestoreAssertionError = (msg) =>
    typeof msg === 'string' &&
    msg.includes('FIRESTORE') &&
    msg.includes('INTERNAL ASSERTION FAILED') &&
    (msg.includes('ID: ca9') || msg.includes('ID: b815'));

  let _reloadScheduled = false;
  const _MAX_RELOADS = 2;
  const _RELOAD_WINDOW_MS = 60000;
  const _scheduleReload = () => {
    if (_reloadScheduled) return;
    try {
      const raw = window.sessionStorage.getItem('_fsReloads');
      const history = raw ? JSON.parse(raw) : [];
      const now = Date.now();
      const recent = history.filter(t => now - t < _RELOAD_WINDOW_MS);
      if (recent.length >= _MAX_RELOADS) {
        console.error('[firebaseConfig] Firestore SDK broken state detected, but reload limit reached (' + _MAX_RELOADS + ' in ' + (_RELOAD_WINDOW_MS / 1000) + 's). Suppressing reload to avoid loop.');
        return;
      }
      recent.push(now);
      window.sessionStorage.setItem('_fsReloads', JSON.stringify(recent));
    } catch {}
    _reloadScheduled = true;
    console.warn('[firebaseConfig] Firestore SDK entered broken state (known SDK bug #9267). Clearing IndexedDB and reloading...');
    const _clearAndReload = async () => {
      try {
        const dbs = await window.indexedDB.databases();
        const firestoreDbs = dbs.filter(d => d.name && (d.name.startsWith('firestore/') || d.name.startsWith('firebaseLocalStorage')));
        await Promise.all(firestoreDbs.map(d => new Promise(r => { const req = window.indexedDB.deleteDatabase(d.name); req.onsuccess = r; req.onerror = r; req.onblocked = r; })));
      } catch {}
      window.location.reload();
    };
    setTimeout(_clearAndReload, 1500);
  };

  window.addEventListener('unhandledrejection', (event) => {
    const msg = event?.reason?.message || String(event?.reason || '');
    if (_isFirestoreAssertionError(msg)) _scheduleReload();
  });

  window.addEventListener('error', (event) => {
    const msg = event?.message || String(event?.error?.message || '');
    if (_isFirestoreAssertionError(msg)) _scheduleReload();
  });
}

// Realtime Database (used for community presence)
let rtdb = null;
try {
  rtdb = getDatabase(app);
} catch (e) {
  console.warn('[firebaseConfig] Realtime Database unavailable:', e.message);
}

// Cloud Functions
let fns = null;
try {
  fns = getFunctions(app);
} catch (e) {
  console.warn('[firebaseConfig] Cloud Functions unavailable:', e.message);
}

